import { getSymbol, Input, Select, Switch } from "@xola/ui-kit";
import { usePreventScrollPropagation } from "common";
import { FormikErrors } from "formik";
import { isNumber } from "lodash";
import React from "react";
import { CouponAlgorithm, CouponScope, LineItemType, SelectionState, TermsTemplateFee } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useFocus } from "../../../hooks/useFocus";
import { SelectedLineItemTemplateListInput } from "../../Common/SelectedLineItemTemplateListInput";
import { ErrorTooltip } from "../../Field/ErrorTooltip";
import { useEquipmentResourcesWithTemplates } from "@/hooks/resources/useEquipmentResourcesWithTemplates";
import { useHasEquipment } from "@/hooks/useHasEquipment";
import { useLocalizationStore } from "@/stores/localizationStore";

interface TermsTemplatePurchaseRuleInputProps {
    value: TermsTemplateFee;
    onChange: (value: TermsTemplateFee) => void;
    field: "discount" | "commission";
    errors?: FormikErrors<TermsTemplateFee>;
    autoFocus?: boolean;
}

export const TermsTemplatePurchaseRuleInput = ({
    value: values,
    onChange,
    field,
    errors,
    autoFocus = false,
}: TermsTemplatePurchaseRuleInputProps) => {
    const seller = useSeller();
    const hasEquipment = useHasEquipment();
    // Templates outlive the equipment they belong to, so restrict the picker to live resources.
    const equipmentTemplateIds = useEquipmentResourcesWithTemplates().map((resource) => resource.templateId);
    const locale = useLocalizationStore().getLocale();

    const inputRef = useFocus(autoFocus);
    usePreventScrollPropagation({ inputRef, enabled: true });

    // A per-reservation rule covers the whole booking, so it offers both unit types.
    const showBothUnitTypes = hasEquipment && values.scope === CouponScope.PER_BOOKING;

    const isAddonsSwitchVisible =
        values.scope === CouponScope.PER_BOOKING &&
        values.algorithm === CouponAlgorithm.PERCENT &&
        (values.demographics.all || values.demographics.selected.length > 0);

    const handleChange = (field: keyof TermsTemplateFee, value: any) => {
        onChange({ ...values, [field]: value });
    };

    const handleAlgorithmChange = (algorithm: CouponAlgorithm) => {
        const scope = algorithm === CouponAlgorithm.PERCENT ? CouponScope.PER_BOOKING : values.scope;
        onChange({ ...values, scope, algorithm });
    };

    const handleScopeChange = (scope: CouponScope) => {
        const includeAddOns = scope === CouponScope.PER_PERSON ? false : values.includeAddOns;
        onChange({ ...values, scope, includeAddOns });
    };

    const handleEquipmentChange = (equipments: SelectionState) => {
        const includeAddOns = equipments.all ? values.includeAddOns : false;
        onChange({ ...values, equipments, includeAddOns });
    };

    const handleDemographicsChange = (demographics: SelectionState) => {
        const includeAddOns = demographics.all ? values.includeAddOns : false;
        onChange({ ...values, demographics, includeAddOns });
    };

    // The picker can change both unit selections at once, so they are applied in a single update.
    const handleUnitSelectionsChange = (demographics: SelectionState, equipments: SelectionState) => {
        const includeAddOns = demographics.all || equipments.all ? values.includeAddOns : false;
        onChange({ ...values, demographics, equipments, includeAddOns });
    };

    return (
        <div className="space-y-2">
            <div className="flex space-x-2">
                <div className="inline-flex">
                    <ErrorTooltip label="Amount" name={`${field}.amount`}>
                        <Input
                            ref={inputRef}
                            type="number"
                            className="relative h-10 !w-25 rounded-r-none"
                            value={isNumber(values.amount) ? Math.abs(values.amount) : ""}
                            isError={!!errors?.amount}
                            title={errors?.amount}
                            onChange={(e: any) => {
                                handleChange("amount", e.target.value ? Number(e.target.value) : "");
                            }}
                        />
                    </ErrorTooltip>

                    <Select
                        className="-ml-[1px] inline-block h-10 w-20 rounded-l-none pr-8 hover:relative"
                        data-testid="terms-template-purchase-rule-input-algorithm"
                        value={values.algorithm}
                        onChange={(e: any) => {
                            handleAlgorithmChange(e.target.value);
                        }}
                    >
                        <option value={CouponAlgorithm.ABSOLUTE} className="no-translate">
                            {getSymbol(seller.currency, locale)}
                        </option>
                        <option value={CouponAlgorithm.PERCENT}>%</option>
                    </Select>
                </div>

                <Select
                    className="h-10 flex-1"
                    data-testid="terms-template-purchase-rule-input-scope"
                    value={values.scope}
                    onChange={(e: any) => {
                        handleScopeChange(e.target.value);
                    }}
                >
                    <option value={CouponScope.PER_BOOKING}>Per Reservation</option>
                    {values.algorithm === CouponAlgorithm.ABSOLUTE ? (
                        <option value={CouponScope.PER_PERSON}>Per Person</option>
                    ) : null}
                    {hasEquipment && values.algorithm === CouponAlgorithm.ABSOLUTE ? (
                        <option value={CouponScope.PER_EQUIPMENT}>Per Equipment</option>
                    ) : null}
                </Select>
            </div>

            {values.scope === CouponScope.PER_EQUIPMENT ? (
                <SelectedLineItemTemplateListInput
                    label="Applies To"
                    type={LineItemType.EQUIPMENT}
                    allowedIds={equipmentTemplateIds}
                    value={values.equipments ?? { all: false, selected: [] }}
                    onChange={(value) => {
                        handleEquipmentChange(value);
                    }}
                />
            ) : (
                <SelectedLineItemTemplateListInput
                    label="Applies To"
                    type={LineItemType.DEMOGRAPHIC}
                    value={values.demographics}
                    secondaryType={showBothUnitTypes ? LineItemType.EQUIPMENT : undefined}
                    secondaryValue={values.equipments ?? { all: false, selected: [] }}
                    secondaryAllowedIds={equipmentTemplateIds}
                    sectionLabels={{ primary: "Demographics", secondary: "Equipment" }}
                    onChange={(demographics, secondary) => {
                        if (secondary) {
                            handleUnitSelectionsChange(demographics, secondary);
                        } else {
                            handleDemographicsChange(demographics);
                        }
                    }}
                />
            )}

            <div className="flex space-x-6">
                {isAddonsSwitchVisible ? (
                    <Switch.Group>
                        <Switch
                            isChecked={values.includeAddOns}
                            onChange={(value: boolean) => {
                                handleChange("includeAddOns", value);
                            }}
                        />

                        <Switch.Label direction="right" className="text-gray-darker">
                            Add-ons
                        </Switch.Label>
                    </Switch.Group>
                ) : null}

                {isAddonsSwitchVisible ? (
                    <Switch.Group>
                        <Switch
                            isChecked={values.includeCustomLineItems}
                            onChange={(value: boolean) => {
                                handleChange("includeCustomLineItems", value);
                            }}
                        />

                        <Switch.Label direction="right" className="text-gray-darker">
                            Custom Line Items
                        </Switch.Label>
                    </Switch.Group>
                ) : null}
            </div>
        </div>
    );
};
